// EditableLook → wire Look adapter (#105). The editor's EditableLook is flat with
// "#rrggbb" colours; the cloud Contract's Look nests the palette and uses bare lowercase
// 6-hex (cloud/src/lib/looks.ts validateLook). One place to bridge the two shapes, so the
// editor and the Contract can't silently drift (the review note on #105).

import type { EditableLook } from "./LookPortal";
import type { Look } from "../api";

/** Adapt an editor Look to the self-contained wire Look the camera pulls + renders.
 *  Carries the committed Look's `id` when present (an EDIT — the server updates that Look in
 *  place); omits it for a fresh Look (the server mints a new immutable id). #161. */
export function editableLookToWire(look: EditableLook): Look {
  return {
    ...(look.id ? { id: look.id } : {}),
    name: look.name,
    style: look.style,
    palette: {
      colors: look.colors.map((c) => c.replace(/^#/, "").toLowerCase()),
      mapMode: look.mapMode,
      brightness: look.brightness,
      contrast: look.contrast,
      saturation: look.saturation,
      gamma: look.gamma,
      posterize: look.posterize,
      hueRotate: look.hueRotate,
      negative: look.negative,
      solarize: look.solarize,
      ditherStrength: look.ditherStrength,
      ditherBias: look.ditherBias,
      glyphSet: look.glyphSet,
      glyphSize: look.glyphSize,
      lumaKey: look.lumaKey ?? 0,
      chromaKey: look.chromaKey ?? 0,
      splash: look.splash ?? 0,
      vignette: look.vignette ?? 0,
      vignetteRadius: look.vignetteRadius ?? 0,
      vignetteFeather: look.vignetteFeather ?? 0,
    },
  };
}

/** Adapt a committed wire Look back into the editor's EditableLook — the inverse of
 *  editableLookToWire, so a library Look can be re-opened in the editor to tweak + re-commit.
 *  Colours regain their "#" prefix; the nested palette adjustments flatten onto the Look. The
 *  immutable `id` is threaded into editor state (from the library row, or the body if present)
 *  so a subsequent commit updates this Look in place rather than minting a duplicate (#161). */
export function wireLookToEditable(look: Look, id?: string): EditableLook {
  const p = look.palette;
  const resolvedId = id ?? look.id;
  return {
    ...(resolvedId ? { id: resolvedId } : {}),
    name: look.name,
    style: look.style,
    colors: p.colors.map((c) => `#${c.replace(/^#/, "").toLowerCase()}`),
    mapMode: p.mapMode,
    brightness: p.brightness,
    contrast: p.contrast,
    saturation: p.saturation,
    // Tone ops (ADR-0033, #164 P4) — fall back to identity for a Look committed before they existed.
    gamma: p.gamma ?? 100,
    posterize: p.posterize ?? 0,
    hueRotate: p.hueRotate ?? 0,
    negative: p.negative ?? false,
    solarize: p.solarize ?? false,
    // Dither knobs (ADR-0033, #164 P5) — fall back to identity for a Look committed before they existed.
    ditherStrength: p.ditherStrength ?? 100,
    ditherBias: p.ditherBias ?? 0,
    glyphSet: p.glyphSet ?? "dots",
    glyphSize: p.glyphSize ?? 100,
    // Selective keys (ADR-0043, #235) — fall back to off for a Look committed before they existed.
    lumaKey: p.lumaKey ?? 0,
    chromaKey: p.chromaKey ?? 0,
    splash: p.splash ?? 0,
    vignette: p.vignette ?? 0,
    vignetteRadius: p.vignetteRadius ?? 0,
    vignetteFeather: p.vignetteFeather ?? 0,
  };
}
